import ApiService from "./api.service";
import Vue from "vue";
import { getCurrentEnvironment } from "@/core/config/hapi/environments";
import { hapiConfig } from "@/core/config/hapi/config";
import VueAxios from "vue-axios";

class HapiServiceClass {
  apiService = undefined;

  constructor() {
    const environmentConfig = getCurrentEnvironment();

    this.apiService = new ApiService(
      "Marketplace ApiService",
      environmentConfig.apiBaseUrl,
      environmentConfig.token
    );

    // only use marketplace axios with Vue
    Vue.use(VueAxios, this.apiService.axiosInstance);
  }

  searchProducts(searchParams) {
    return this.apiService.query("products/search/", searchParams);
  }

  retrieveProduct(productId) {
    return this.apiService.get(`products/single/${productId}/`);
  }

  retrieveContract(contractId) {
    return this.apiService.get(`contracts/single/${contractId}/`);
  }

  retrieveContracts(contractIds) {
    return this.apiService.get(`contracts/multiple/${contractIds.join(",")}/`);
  }

  addContract(
    customerId,
    channelId,
    credentials,
    facets,
    followed_instructions = null,
    expiry_date = null,
    purchase_price_amount = null,
    purchase_price_currency = null,
    number_of_credits = null,
    alias = null,
    group_id,
    posting_duration_days = null
  ) {
    let req = {
      customer_id: customerId,
      channel_id: channelId,
      credentials: credentials,
      group_id,
      facets: facets
    };
    if (followed_instructions)
      req["followed_instructions"] = followed_instructions;

    if (expiry_date) req["expiry_date"] = expiry_date;
    if (alias) req["alias"] = alias;

    if (number_of_credits) req["credits"] = number_of_credits;
    if (posting_duration_days && posting_duration_days > 0)
      req["posting_duration_days"] = posting_duration_days;

    if (purchase_price_amount && purchase_price_currency)
      req["purchase_price"] = {
        amount: purchase_price_amount,
        currency: purchase_price_currency
      };

    return this.apiService.post("contracts/", req).catch(error => {
      if (error.response.status >= 400 && error.response.status < 500) {
        return error.response;
      }
      throw error;
    });
  }

  changeEnvironment(environment) {
    const environmentConfig = hapiConfig[environment];
    this.apiService.url = environmentConfig.apiBaseUrl;

    // only set options on Vue for the default axios which is marketplace
    Vue.axios.defaults.baseURL = environmentConfig.apiBaseUrl;
    Vue.axios.defaults.headers = this.apiService.defaultHeaders;
  }

  listContracts(params) {
    return this.apiService.get(`contracts/`, {
      params
    });
  }

  listContractGroups() {
    return this.apiService.get("igb/contracts/groups/");
  }

  createContractGroup(body) {
    return this.apiService.post("igb/contracts/groups/", body);
  }

  listMultiContract(ids) {
    return this.apiService.get(`contracts/multiple/${ids.join(",")}/`);
  }

  listEducationLevels() {
    return this.apiService.get(`taxonomy/education-levels`);
  }

  listSeniorities() {
    return this.apiService.get(`taxonomy/seniority`);
  }

  listIndustries() {
    return this.apiService.get(`products/industries/`);
  }

  orderCampaign(campaignBody) {
    return this.apiService
      .post("campaigns/order", campaignBody)
      .catch(error => {
        if (error.response.status === 400 || error.response.status === 422) {
          return error.response;
        }
        throw error;
      });
  }

  editCampaign(campaignBody) {
    return this.apiService
      .put(`campaigns/${campaignBody.campaignId}/edit/`, campaignBody)
      .catch(error => {
        if (error.response.status === 400 || error.response.status === 422) {
          return error.response;
        }
        throw error;
      });
  }

  listCampaigns(limit = 20, offset = 0) {
    return this.apiService.query("campaigns", { limit: limit, offset: offset, includeDrafts: true });
  }

  retrieveCampaign(campaignId) {
    return this.apiService.get(`campaigns/${campaignId}`);
  }

  listChannelsMocs() {
    return this.apiService.get("products/channels/mocs/");
  }

  retrieveChannelMoc(channelId) {
    return this.apiService.get(`products/channels/mocs/`, channelId);
  }

  getWallet() {
    return this.apiService.get(`wallet`);
  }

  createWallet(currency = "USD") {
    return this.apiService.post(`wallet`, {
      customerName: Vue.prototype.$auth.user.name,
      currency
    });
  }

  retrieveBillingPortalLink() {
    return this.apiService.post(`wallet/billing-portal`);
  }

  askCampaignStatus(campaignId) {
    return this.apiService.get(`campaigns/${campaignId}/status`);
  }

  takeCampaignOffline(campaignId) {
    const body = {
      campaignId,
      status: "offline"
    };
    return this.apiService.put(`campaigns/${campaignId}/cancellation`, body);
  }

  getATSUserSetting() {
    return this.apiService.get("v3/ats/atsuser/me/settings/");
  }
}

export const HapiService = new HapiServiceClass();
